import config from "../../config";
import { patientSearchParams } from "../TreatmentPlans/lib/patientSearch";
import { withOverageRetry } from "../TreatmentPlans/usageOverage";

// Mirrors the auth pattern used across the other controllers (TreatmentPlans).
function authHeader() {
  const user = JSON.parse(localStorage.getItem("user"));
  if (user && user.key) {
    return { Authorization: "Token  " + user.key };
  }
  return {};
}

function officeId() {
  return localStorage.getItem("selectedOffice");
}

async function readJson(response) {
  try {
    return await response.json();
  } catch (e) {
    return null;
  }
}

/**
 * The office's requests, one row per request.
 * GET /clinics/insurance-requests/?office_id=&status=&outcome=&q=&request_type=&page=&page_size=
 *
 * `status` is the OFFICE vocabulary — ready | in_progress | archived | all — not
 * the internal open/fulfilled/cancelled. `outcome` narrows Ready to one of its
 * filters (outcomes.js). The response carries per-tab `counts` and per-filter
 * `outcome_counts`, each computed independently of what is open, and
 * `can_manage` — whether this user may set statuses and archive.
 */
export const getRequests = async ({ status = "ready", outcome, q, requestType, page = 1, pageSize = 25 } = {}) => {
  const headers = Object.assign({ "Content-Type": "application/json" }, authHeader());
  let query = `?office_id=${encodeURIComponent(officeId())}&status=${encodeURIComponent(status)}`;
  query += `&page=${page}&page_size=${pageSize}`;
  if (outcome) query += `&outcome=${encodeURIComponent(outcome)}`;
  if (q) query += `&q=${encodeURIComponent(q)}`;
  if (requestType) query += `&request_type=${encodeURIComponent(requestType)}`;

  const response = await fetch(
    new Request(`${config.apiGateway.URL}/clinics/insurance-requests/${query}`, {
      method: "GET",
      headers,
    }),
  );
  return { json: await readJson(response), status: response.status };
};

/**
 * Submit a request for one or more of a patient's policies.
 *
 * The server decides whether an agent or the FTC team services it — the caller
 * neither knows nor cares. A 400 with `error_type: "incomplete_policies"` means
 * a selected policy is missing data the verification needs; the payload names
 * the fields per policy so the composer can send the user back to fix them.
 *
 * Wrapped in withOverageRetry so a usage cap surfaces the standard confirm
 * dialog rather than a bare failure.
 */
export const createRequest = async ({ patientId, policyIds, requestType, urgency = "normal", estimates }) => {
  return withOverageRetry(async (confirmOverage) => {
    const headers = Object.assign({ "Content-Type": "application/json" }, authHeader());
    if (confirmOverage) headers["X-Confirm-Overage"] = "1";
    const body = {
      patient_id: patientId,
      policy_ids: policyIds,
      request_type: requestType,
      urgency,
    };
    // Estimate requests carry the submissions to chase, keyed by policy. Omitted
    // entirely for a breakdown, which needs nothing beyond the policy.
    if (estimates) body.estimates = estimates;
    if (confirmOverage) body.confirm_overage = true;

    const response = await fetch(
      new Request(`${config.apiGateway.URL}/clinics/insurance-requests/`, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
      }),
    );
    return { json: await readJson(response), status: response.status };
  });
};

/**
 * Send a request that is waiting on the office back to our team, with the
 * office's reply. One call answers every item our team handed back (Needs
 * resubmission / Details Needed by Smilepass), with an optional new file each, AND
 * lifts a Details Needed by Smilepass flag on the policy; the server refuses it when
 * nothing is waiting.
 */
export const resubmitRequest = async (requestId, note, files, extra) => {
  // With files it is multipart — one `document_<item id>` per item the office
  // is answering with a file; the browser sets the multipart boundary, so no
  // Content-Type here. Without, plain JSON. `extra` carries the office's
  // answers to a Details Needed checklist ({ answers, skipped }).
  const fileEntries = Object.entries(files || {}).filter(function (entry) { return !!entry[1]; });
  const answers = extra && extra.answers ? extra.answers : null;
  const skipped = extra && extra.skipped ? extra.skipped : null;
  let headers;
  let body;
  if (fileEntries.length) {
    headers = authHeader();
    body = new FormData();
    body.append("note", note || "");
    if (answers) body.append("answers", JSON.stringify(answers));
    if (skipped) body.append("skipped", JSON.stringify(skipped));
    fileEntries.forEach(function (entry) { body.append("document_" + entry[0], entry[1]); });
  } else {
    headers = Object.assign({ "Content-Type": "application/json" }, authHeader());
    const payload = { note: note || "" };
    if (answers) payload.answers = answers;
    if (skipped) payload.skipped = skipped;
    body = JSON.stringify(payload);
  }
  const response = await fetch(
    new Request(`${config.apiGateway.URL}/clinics/insurance-requests/${requestId}/resubmit/`, {
      method: "POST",
      headers,
      body,
    }),
  );
  return { json: await readJson(response), status: response.status };
};

/**
 * The office's own follow-through on a Ready request — "contact_patient",
 * "appointment_booked", or null to clear. Practice staff only (the server
 * refuses FTC personnel).
 */
export const setOfficeStatus = async (requestId, officeStatus) => {
  const headers = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const response = await fetch(
    new Request(`${config.apiGateway.URL}/clinics/insurance-requests/${requestId}/office-status/`, {
      method: "PUT",
      headers,
      body: JSON.stringify({ office_status: officeStatus || null }),
    }),
  );
  return { json: await readJson(response), status: response.status };
};

/** Archive a Ready request (an open one is withdrawn as it is archived). */
export const archiveRequest = async (requestId) => {
  const headers = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const response = await fetch(
    new Request(`${config.apiGateway.URL}/clinics/insurance-requests/${requestId}/archive/`, {
      method: "POST",
      headers,
      body: JSON.stringify({}),
    }),
  );
  return { json: await readJson(response), status: response.status };
};

/** Move an archived request back to Ready (not one withdrawn when archived). */
export const restoreRequest = async (requestId) => {
  const headers = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const response = await fetch(
    new Request(`${config.apiGateway.URL}/clinics/insurance-requests/${requestId}/archive/`, {
      method: "DELETE",
      headers,
    }),
  );
  return { json: await readJson(response), status: response.status };
};

/** Withdraw an in-progress request. */
export const cancelRequest = async (requestId) => {
  const headers = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const response = await fetch(
    new Request(`${config.apiGateway.URL}/clinics/insurance-requests/${requestId}/cancel/`, {
      method: "POST",
      headers,
      body: JSON.stringify({}),
    }),
  );
  return { json: await readJson(response), status: response.status };
};

/**
 * Permanently delete a request. Super-admins only — the server enforces it, this
 * is just the call. Distinct from cancelRequest above: withdrawing closes a row
 * and keeps it, this removes it. The breakdown or estimate it produced is NOT
 * affected; see the backend's delete_request docstring for exactly what stays.
 */
export const deleteRequest = async (requestId) => {
  const headers = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const response = await fetch(
    new Request(`${config.apiGateway.URL}/clinics/insurance-requests/${requestId}/`, {
      method: "DELETE",
      headers,
    }),
  );
  // 204 carries no body, so readJson would only ever return null here.
  return { json: response.status === 204 ? null : await readJson(response), status: response.status };
};

/**
 * Patient search for the composer, scoped to the selected office.
 *
 * Reuses `PatientList` (the endpoint the Patients board is built
 * on) and the shared term-shape routing, so "what does typing X match" cannot
 * drift between the two surfaces.
 */
export const searchPatients = async (query, { page = 1, pageSize = 12 } = {}) => {
  const headers = Object.assign({ "Content-Type": "application/json" }, authHeader());
  let url = `${config.apiGateway.URL}/clinics/patient-list/?office_id=${encodeURIComponent(officeId())}`;
  url += `&page=${page}&page_size=${pageSize}`;
  const params = patientSearchParams(query);
  Object.keys(params).forEach((key) => {
    if (params[key]) url += `&${key}=${encodeURIComponent(params[key])}`;
  });

  const response = await fetch(new Request(url, { method: "GET", headers }));
  return { json: await readJson(response), status: response.status };
};

/**
 * Add a patient who isn't in Smilepass yet — usually one the office has just
 * created in its PMS, before the nightly sync brings them in.
 *
 * The same endpoint the Patients board's "Add New" posts to. `check_duplicates`
 * makes the server answer 409 `possible_duplicates` — with the office's patients
 * who share both names and the date of birth — instead of creating a second
 * record for someone already here; `force` creates anyway. The PMS sync later
 * links the new patient to their PMS record by that same name + DOB rule.
 *
 * Optional fields are sent only when filled in: the server's phone, province and
 * postal-code validators reject an empty string.
 */
export const createPatient = async (
  { firstName, lastName, birthDate, phone, email, address, city, state, zipCode },
  { force = false } = {},
) => {
  const headers = Object.assign({ "Content-Type": "application/json" }, authHeader());
  const body = { first_name: firstName, last_name: lastName, birth_date: birthDate };
  if (phone) body.phone = phone;
  if (email) body.email = email;
  if (address) body.address = address;
  if (city) body.city = city;
  if (state) body.state = state;
  if (zipCode) body.zip_code = zipCode;
  let url = `${config.apiGateway.URL}/clinics/create-patient/?office_id=${encodeURIComponent(officeId())}`;
  url += "&check_duplicates=1";
  if (force) url += "&force=1";

  const response = await fetch(new Request(url, { method: "POST", headers, body: JSON.stringify(body) }));
  return { json: await readJson(response), status: response.status };
};

export const controller = {
  getRequests,
  createRequest,
  resubmitRequest,
  setOfficeStatus,
  archiveRequest,
  restoreRequest,
  cancelRequest,
  deleteRequest,
  searchPatients,
  createPatient,
};

export default controller;
